import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import moment from 'moment';
import axios from 'axios';
import './CalendarLayout.css';

const API_BASE_URL = 'https://asia-northeast3-nawaplay-6dadb.cloudfunctions.net/api';

const DEPARTMENT_COLORS = {
  '개나리부': '#fbc02d',
  '국화부': '#e57373',
  '전국신인부': '#64b5f6',
  '신인부': '#64b5f6',
  '지역신인부': '#64b5f6',
  '지역 신인부': '#64b5f6',
  '챌린저부': '#81c784',
  '마스터스부': '#ba68c8',
  '오픈부': '#ffb74d',
  '남자오픈부': '#ffb74d',
  '지도자부': '#90a4ae',
  '베테랑부': '#a1887f',
  '세미오픈부': '#ffb74d',
  '혼합복식부': '#ff8a65',
  '혼합복식': '#ff8a65',
  '남자부': '#4fc3f7',
  '여자부': '#f06292',
  '남자퓨처스부': '#4fc3f7',
  '여자퓨처스부': '#f06292',
  '부부조': '#ff8a65',
  '부자조': '#ff8a65',
  '혼복부': '#ff8a65',
  '초급자부(남자)': '#4fc3f7',
  '초급자부(여자)': '#f06292',
};

const ALL_DEPARTMENTS = Object.keys(DEPARTMENT_COLORS);

const CalendarLayout = () => {
  const [tournaments, setTournaments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filters, setFilters] = useState(
    ALL_DEPARTMENTS.reduce((acc, d) => ({ ...acc, [d]: true }), {})
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournaments = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API_BASE_URL}/tournaments`);
        setTournaments(res.data);
      } catch (err) {
        setError('토너먼트 목록을 불러오는 데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchTournaments();
  }, []);

  // 날짜별 이벤트 수 계산
  const eventCountByDate = {};
  tournaments.forEach(t => {
    if (t.departments && Array.isArray(t.departments)) {
      t.departments.forEach(dep => {
        if (filters[dep.department]) {
          const dateStr = moment(dep.matchDate).format('YYYY-MM-DD');
          if (!eventCountByDate[dateStr]) eventCountByDate[dateStr] = 0;
          eventCountByDate[dateStr]++;
        }
      });
    }
  });

  // 선택한 날짜의 이벤트 목록
  const eventsForSelectedDate = selectedDate
    ? tournaments.flatMap(t =>
        (t.departments || [])
          .filter(dep =>
            filters[dep.department] &&
            moment(dep.matchDate).isSame(selectedDate, 'day')
          )
          .map(dep => ({
            ...dep,
            tournamentName: t.name,
            organization: t.organization,
            status: dep.status,
          }))
      )
    : [];

  // 부서별 필터 토글
  const toggleFilter = dep => {
    setFilters(prev => ({ ...prev, [dep]: !prev[dep] }));
  };

  // 전체 선택/해제
  const allSelected = ALL_DEPARTMENTS.every(dep => filters[dep]);
  const toggleAll = () => {
    const newValue = !allSelected;
    setFilters(ALL_DEPARTMENTS.reduce((acc, d) => ({ ...acc, [d]: newValue }), {}));
  };

  return (
    <div className="calendar-layout-root">
      <aside className="calendar-sidebar">
        <div className="calendar-mini">
          <Calendar
            locale="en-US"
            onChange={setSelectedDate}
            value={selectedDate}
            tileContent={({ date, view }) => {
              const dateStr = moment(date).format('YYYY-MM-DD');
              const count = eventCountByDate[dateStr] || 0;
              return count > 0 ? (
                <span className="event-badge">{count}</span>
              ) : null;
            }}
          />
        </div>
        <div className="calendar-legend mt-3">
          <div className="legend-title">범례/부서별 필터</div>
          <button className="legend-all-btn" onClick={toggleAll}>
            {allSelected ? '전체 해제' : '전체 선택'}
          </button>
          <div className="legend-list">
            {ALL_DEPARTMENTS.map(dep => (
              <div
                key={dep}
                className="legend-item"
                style={{ opacity: filters[dep] ? 1 : 0.4 }}
                onClick={() => toggleFilter(dep)}
              >
                <span
                  className="legend-color"
                  style={{ backgroundColor: DEPARTMENT_COLORS[dep] }}
                />
                {dep}
              </div>
            ))}
          </div>
        </div>
      </aside>
      <main className="calendar-main">
        <h3 className="mb-3">
          {selectedDate ? moment(selectedDate).format('YYYY년 MM월 DD일') + ' 이벤트' : '날짜를 선택하세요'}
        </h3>
        {loading ? (
          <div>로딩 중...</div>
        ) : error ? (
          <div className="text-danger">{error}</div>
        ) : eventsForSelectedDate.length === 0 ? (
          <div className="text-muted">이 날짜에 해당하는 이벤트가 없습니다.</div>
        ) : (
          <div className="event-list">
            {eventsForSelectedDate.map((event, idx) => (
              <div key={idx} className="event-card">
                <div className="event-title">
                  <b>{event.organization} - {event.tournamentName}</b>
                  <span className="event-status">{event.status}</span>
                </div>
                <div className="event-info">
                  <span>부서: {event.department}</span>
                  <span>장소: {event.location}</span>
                  <span>일시: {moment(event.matchDate).format('YYYY-MM-DD')}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CalendarLayout; 